// Aproximaciones sRGB de colores Pantone para el menú del `#`
// (ADR-2026-09-21-pantone-en-el-menu-de-colores). OJO: Pantone es un sistema de tintas y
// telas; estos hex son los valores de pantalla que Pantone publica como referencia (los
// Color of the Year y los de la carta gráfica "C" que usan las guías de marca), no una
// conversión oficial ni exacta. Se los muestra como "≈".
//
// Códigos: `NN-NNNN TCX` es la carta textil (moda), `NNN C` la gráfica sobre papel
// estucado. Acá vive la tabla CORTA (los destacados y los que tienen nombre en castellano);
// la COMPLETA —3.651 filas, TCX y "C", generada por `scripts/pantone/generar.mjs` en
// `pantoneTabla.json`— pesa 42 KB comprimida y se carga a demanda con `cargarTablaPantone`
// la primera vez que alguien busca; hasta entonces se busca en la corta.

export interface ColorPantone {
  codigo: string;
  nombre: string;
  hex: string;
}

export const PANTONE: readonly ColorPantone[] = [
  // Color of the Year, del más nuevo al más viejo.
  { codigo: '11-4201 TCX', nombre: 'Cloud Dancer', hex: '#F0EEE9' },
  { codigo: '17-1230 TCX', nombre: 'Mocha Mousse', hex: '#A47864' },
  { codigo: '13-1023 TCX', nombre: 'Peach Fuzz', hex: '#FFBE98' },
  { codigo: '18-1750 TCX', nombre: 'Viva Magenta', hex: '#BB2649' },
  { codigo: '17-3938 TCX', nombre: 'Very Peri', hex: '#6667AB' },
  { codigo: '13-0647 TCX', nombre: 'Illuminating', hex: '#F5DF4D' },
  { codigo: '17-5104 TCX', nombre: 'Ultimate Gray', hex: '#939597' },
  { codigo: '19-4052 TCX', nombre: 'Classic Blue', hex: '#0F4C81' },
  { codigo: '16-1546 TCX', nombre: 'Living Coral', hex: '#FF6F61' },
  { codigo: '18-3838 TCX', nombre: 'Ultra Violet', hex: '#5F4B8B' },
  { codigo: '15-0343 TCX', nombre: 'Greenery', hex: '#88B04B' },
  { codigo: '13-1520 TCX', nombre: 'Rose Quartz', hex: '#F7CAC9' },
  { codigo: '15-3919 TCX', nombre: 'Serenity', hex: '#92A8D1' },
  { codigo: '18-1438 TCX', nombre: 'Marsala', hex: '#955251' },
  { codigo: '18-3224 TCX', nombre: 'Radiant Orchid', hex: '#B565A7' },
  { codigo: '17-5641 TCX', nombre: 'Emerald', hex: '#009B77' },
  { codigo: '17-1463 TCX', nombre: 'Tangerine Tango', hex: '#DD4124' },
  { codigo: '18-2120 TCX', nombre: 'Honeysuckle', hex: '#D65076' },
  { codigo: '15-5519 TCX', nombre: 'Turquoise', hex: '#45B8AC' },
  { codigo: '14-0848 TCX', nombre: 'Mimosa', hex: '#EFC050' },
  { codigo: '18-3943 TCX', nombre: 'Blue Iris', hex: '#5B5EA6' },
  { codigo: '19-1557 TCX', nombre: 'Chili Pepper', hex: '#9B2335' },
  { codigo: '13-1106 TCX', nombre: 'Sand Dollar', hex: '#DFCFBE' },
  { codigo: '15-5217 TCX', nombre: 'Blue Turquoise', hex: '#55B4B0' },
  { codigo: '17-1456 TCX', nombre: 'Tigerlily', hex: '#E15D44' },
  { codigo: '14-4811 TCX', nombre: 'Aqua Sky', hex: '#7FCDCD' },
  { codigo: '19-1664 TCX', nombre: 'True Red', hex: '#BC243C' },
  { codigo: '17-2031 TCX', nombre: 'Fuchsia Rose', hex: '#C3447A' },
  { codigo: '15-4020 TCX', nombre: 'Cerulean', hex: '#98B4D4' },
  // Textiles de uso corriente.
  { codigo: '11-0601 TCX', nombre: 'Bright White', hex: '#F4F5F0' },
  { codigo: '19-4007 TCX', nombre: 'Anthracite', hex: '#28282D' },
  { codigo: '19-0303 TCX', nombre: 'Jet Black', hex: '#2D2C2F' },
  { codigo: '19-3938 TCX', nombre: 'Twilight Blue', hex: '#313D64' },
  { codigo: '18-4140 TCX', nombre: 'French Blue', hex: '#0072B5' },
  { codigo: '14-4318 TCX', nombre: 'Sky Blue', hex: '#8ABAD3' },
  { codigo: '12-0752 TCX', nombre: 'Buttercup', hex: '#FAE03C' },
  { codigo: '17-1462 TCX', nombre: 'Flame', hex: '#F2552C' },
  { codigo: '18-1664 TCX', nombre: 'Fiery Red', hex: '#D01C1F' },
  { codigo: '19-1763 TCX', nombre: 'Racing Red', hex: '#BD162C' },
  { codigo: '15-1247 TCX', nombre: 'Tangerine', hex: '#F88F58' },
  // Carta gráfica "C": los que aparecen en las guías de marca.
  { codigo: '186 C', nombre: 'Rojo 186', hex: '#C8102E' },
  { codigo: '485 C', nombre: 'Rojo 485', hex: '#DA291C' },
  { codigo: '032 C', nombre: 'Rojo 032', hex: '#EF3340' },
  { codigo: '199 C', nombre: 'Rojo 199', hex: '#D50032' },
  { codigo: '1795 C', nombre: 'Rojo 1795', hex: '#D22630' },
  { codigo: 'Orange 021 C', nombre: 'Naranja 021', hex: '#FE5000' },
  { codigo: '165 C', nombre: 'Naranja 165', hex: '#FF671F' },
  { codigo: '151 C', nombre: 'Naranja 151', hex: '#FF8200' },
  { codigo: 'Yellow C', nombre: 'Amarillo', hex: '#FEDD00' },
  { codigo: '116 C', nombre: 'Amarillo 116', hex: '#FFCD00' },
  { codigo: '1235 C', nombre: 'Amarillo 1235', hex: '#FFB81C' },
  { codigo: '376 C', nombre: 'Verde 376', hex: '#84BD00' },
  { codigo: '382 C', nombre: 'Verde 382', hex: '#C4D600' },
  { codigo: '348 C', nombre: 'Verde 348', hex: '#00843D' },
  { codigo: '355 C', nombre: 'Verde 355', hex: '#009639' },
  { codigo: '342 C', nombre: 'Verde 342', hex: '#006747' },
  { codigo: '3425 C', nombre: 'Verde 3425', hex: '#006341' },
  { codigo: '326 C', nombre: 'Turquesa 326', hex: '#00B2A9' },
  { codigo: '3272 C', nombre: 'Turquesa 3272', hex: '#00A499' },
  { codigo: 'Process Blue C', nombre: 'Process Blue', hex: '#0085CA' },
  { codigo: '2925 C', nombre: 'Celeste 2925', hex: '#009CDE' },
  { codigo: '3005 C', nombre: 'Azul 3005', hex: '#0077C8' },
  { codigo: '300 C', nombre: 'Azul 300', hex: '#005EB8' },
  { codigo: '286 C', nombre: 'Azul 286', hex: '#0033A0' },
  { codigo: 'Reflex Blue C', nombre: 'Reflex Blue', hex: '#001489' },
  { codigo: '288 C', nombre: 'Azul 288', hex: '#002D72' },
  { codigo: '289 C', nombre: 'Azul 289', hex: '#0C2340' },
  { codigo: '2758 C', nombre: 'Azul 2758', hex: '#001E62' },
  { codigo: '292 C', nombre: 'Celeste 292', hex: '#69B3E7' },
  { codigo: '7462 C', nombre: 'Azul 7462', hex: '#00558C' },
  { codigo: '7687 C', nombre: 'Azul 7687', hex: '#1D4F91' },
  { codigo: 'Purple C', nombre: 'Púrpura', hex: '#BB29BB' },
  { codigo: '2685 C', nombre: 'Violeta 2685', hex: '#56008C' },
  { codigo: '2597 C', nombre: 'Violeta 2597', hex: '#5C068C' },
  { codigo: '266 C', nombre: 'Violeta 266', hex: '#753BBD' },
  { codigo: '219 C', nombre: 'Rosa 219', hex: '#DA1884' },
  { codigo: '226 C', nombre: 'Rosa 226', hex: '#D0006F' },
  { codigo: '7499 C', nombre: 'Crema 7499', hex: '#F1E6B2' },
  { codigo: '468 C', nombre: 'Arena 468', hex: '#DDCBA4' },
  { codigo: '7503 C', nombre: 'Caqui 7503', hex: '#A89968' },
  { codigo: '7530 C', nombre: 'Topo 7530', hex: '#A39382' },
  { codigo: '4625 C', nombre: 'Marrón 4625', hex: '#4F2C1D' },
  { codigo: '476 C', nombre: 'Marrón 476', hex: '#4E3629' },
  { codigo: '871 C', nombre: 'Dorado 871', hex: '#85714D' },
  { codigo: '877 C', nombre: 'Plateado 877', hex: '#8A8D8F' },
  { codigo: 'Cool Gray 1 C', nombre: 'Gris frío 1', hex: '#D9D9D6' },
  { codigo: 'Cool Gray 5 C', nombre: 'Gris frío 5', hex: '#B1B3B3' },
  { codigo: 'Cool Gray 9 C', nombre: 'Gris frío 9', hex: '#75787B' },
  { codigo: 'Cool Gray 11 C', nombre: 'Gris frío 11', hex: '#53565A' },
  { codigo: 'Warm Gray 1 C', nombre: 'Gris cálido 1', hex: '#D7D2CB' },
  { codigo: 'Warm Gray 11 C', nombre: 'Gris cálido 11', hex: '#6E6259' },
  { codigo: '7546 C', nombre: 'Azul pizarra 7546', hex: '#253746' },
  { codigo: 'Black C', nombre: 'Negro', hex: '#2D2926' },
  { codigo: 'Black 6 C', nombre: 'Negro 6', hex: '#101820' },
];

/** Los que se ven en la sección "Colores Pantone" sin buscar: los Color of the Year recientes y dos clásicos. */
export const PANTONE_DESTACADOS: readonly string[] = [
  '17-1230 TCX',
  '13-1023 TCX',
  '18-1750 TCX',
  '17-3938 TCX',
  '19-4052 TCX',
  '16-1546 TCX',
  '186 C',
  'Reflex Blue C',
];

const sinAcentos = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const compacto = (s: string): string => sinAcentos(s).replace(/[\s-]+/g, '');

// La tabla completa, cuando ya se cargó: la corta primero (manda su nombre en castellano) y
// después el resto por código, sin repetir.
let tablaCompleta: readonly ColorPantone[] | null = null;
let cargando: Promise<void> | null = null;

export const tablaPantoneCargada = (): boolean => tablaCompleta !== null;

/** Carga la tabla completa una sola vez (import dinámico del JSON). Idempotente. */
export function cargarTablaPantone(): Promise<void> {
  if (tablaCompleta) return Promise.resolve();
  if (!cargando) {
    cargando = import('./pantoneTabla.json')
      .then((m) => {
        const filas = (m.default ?? m) as unknown as [string, string, string][];
        const vistos = new Set(PANTONE.map((c) => c.codigo));
        const resto: ColorPantone[] = [];
        for (const [codigo, nombre, hex] of filas) {
          if (vistos.has(codigo)) continue;
          vistos.add(codigo);
          resto.push({ codigo, nombre: nombre || codigo, hex });
        }
        tablaCompleta = [...PANTONE, ...resto];
      })
      .catch(() => {
        // Sin la tabla completa se sigue con la corta; el próximo intento vuelve a cargar.
        cargando = null;
      });
  }
  return cargando;
}

const tabla = (): readonly ColorPantone[] => tablaCompleta ?? PANTONE;

/** ¿Lo escrito parece un código Pantone? ("19-4052", "194052", "186c", "186 C"). */
export const pareceCodigoPantone = (consulta: string): boolean => /^\d{2}-?\d{2,4}$|^\d{3,4}\s*c?$/i.test(consulta.trim());

/** Busca por código (con o sin guión y espacios), por nombre o por hex en la tabla que haya
 *  (la completa si ya se cargó); sin consulta, los primeros. Los que EMPIEZAN por lo
 *  buscado van antes que los que sólo lo contienen ("186" → 186 C antes que 2186 C). */
export function buscarPantone(consulta: string, limite = 30): ColorPantone[] {
  const q = compacto(consulta);
  const qNombre = sinAcentos(consulta.trim());
  const qHex = q.replace(/^#/, '');
  if (!q) return tabla().slice(0, limite);
  const empiezan: ColorPantone[] = [];
  const contienen: ColorPantone[] = [];
  for (const c of tabla()) {
    const codigo = compacto(c.codigo);
    const nombre = sinAcentos(c.nombre);
    if (codigo.startsWith(q) || nombre.startsWith(qNombre) || c.hex.slice(1).toLowerCase().startsWith(qHex)) empiezan.push(c);
    else if (codigo.includes(q) || nombre.includes(qNombre)) contienen.push(c);
    if (empiezan.length >= limite) break;
  }
  return [...empiezan, ...contienen].slice(0, limite);
}

export const pantonePorHex = (hex: string): ColorPantone | undefined => tabla().find((c) => c.hex === hex);

/** Cómo queda en el mensaje, después del hex: "(Pantone 19-4052 TCX Classic Blue)"; sin
 *  nombre propio (la carta "C"), sólo el código: "(Pantone 277 C)". */
export const rotuloDePantone = (c: ColorPantone): string =>
  `(Pantone ${c.codigo}${c.nombre && c.nombre !== c.codigo ? ` ${c.nombre}` : ''})`;
